import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import DropdownSelectFilter from '../../../../Filters/DropdownSelectFilter';
import { FiltersInitialValue, HotelRatingFilterOptions, HotelRatings } from '../../../../../constants';
import { hotelSearch } from '../../../../../store/slices/hotel';
import { setHotelTripSearchInputs } from '../../../../../store/slices/tripSearch';

const RatingHotelFilter = ({ tripId, variant, filterBgColor }) => {
  const { travelAgency, isLoggedIn } = useSelector((store) => store.user);
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isHotelTripSearch = !tripId;
  const {
    searchObj = {},
    defaultSearchObj = {},
  } = useSelector((store) => store.hotel.hotelSearches[tripId] || {});
  const hotelTripSearchInputs = useSelector((store) => store.tripSearch.searchInputs.hotel);
  const value = isHotelTripSearch ? hotelTripSearchInputs.filterRating : searchObj.filterRating;
  const defaultValue = isHotelTripSearch ? FiltersInitialValue.hotel.filterRating
    : defaultSearchObj.filterRating;
  const defaultStarRating = isLoggedIn ? +travelAgency?.filterSettings?.defaultStarRating
    : +travelAgency?.filterSettings?.defaultStarRatingCFP;
  const isDefaultRatingValid = Object.values(HotelRatings).includes(defaultStarRating);

  const handleChange = useCallback((newValue) => {
    const payload = {
      tripId,
      searchObj: {
        page: 1,
        filterRating: newValue,
      },
    };
    if (isHotelTripSearch) {
      dispatch(setHotelTripSearchInputs({ ...payload.searchObj }));
    } else {
      dispatch(hotelSearch(payload));
    }
    track('ratingHotelFilter.handleChange', {
      filterRating: newValue,
    });
  }, [dispatch, isHotelTripSearch, track, tripId]);

  const hotelRatingFilterOptionsToDisplay = useMemo(() => {
    if (
      travelAgency
      && travelAgency.filterSettings
      && travelAgency.filterSettings.doShowHotelRatingInDescendingOrder
    ) {
      const anyStarRatingOption = _.find(HotelRatingFilterOptions,
        (f) => f.value === HotelRatings.ANY);
      const descendingOptions = _.orderBy(_.filter(HotelRatingFilterOptions,
        (r) => r.value !== HotelRatings.ANY), 'value', 'desc');
      if (!anyStarRatingOption) return descendingOptions;
      return [anyStarRatingOption, ...descendingOptions];
    }
    return HotelRatingFilterOptions;
  }, [travelAgency]);

  useEffect(() => {
    if (isDefaultRatingValid && value === undefined) {
      handleChange(defaultStarRating);
    }
  }, [defaultStarRating, handleChange, isDefaultRatingValid, value]);

  return travelAgency && travelAgency.filterSettings && travelAgency.filterSettings.hideRatingsInput
    ? <></> : (
      <DropdownSelectFilter
        label="Rating"
        onChange={handleChange}
        options={hotelRatingFilterOptionsToDisplay}
        defaultOptionValue={defaultValue}
        selectedOptionValue={value}
        menuWidth="w-48"
        filterButtonMaxWidth="max-w-full"
        variant={variant}
        bgClassName={!isHotelTripSearch ? '' : filterBgColor}
        withClear
      />
    );
};

RatingHotelFilter.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  filterBgColor: PropTypes.string,
};

RatingHotelFilter.defaultProps = {
  tripId: undefined,
  variant: 'outline',
  filterBgColor: undefined,
};

export default RatingHotelFilter;
